import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Button,
	Chip,
	Collapse,
	Divider,
	Drawer,
	FormControl,
	Grid,
	IconButton,
	Input,
	InputLabel,
	LinearProgress,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import {
	mergeSharedAdvancedFilters,
	updateSharedPartesFiltersState,
} from "../sharedPartesFiltersSession";

const OPERATIVOS_ADVANCED_FILTERS_SESSION_KEY = "planaett:operativos:advanced-filters";
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MENU_PROPS = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 300,
		},
	},
};

const STATIC_FILTERS = [
	{
		key: "validado",
		nombre: "Validado",
		valores: [
			{ value: "1", label: "Si" },
			{ value: "0", label: "No" },
		],
	},
	{
		key: "revisado",
		nombre: "Revisado",
		valores: [
			{ value: "1", label: "Si" },
			{ value: "0", label: "No" },
		],
	},
];
const OPERATIVOS_ADVANCED_FILTER_KEYS = new Set([
	"campanas",
	"empresas",
	"delegaciones",
	"cabos",
	"validado",
	"revisado",
]);

const getOptionValue = (option) => String(option?.value ?? option?.nombre ?? "");
const getOptionLabel = (option) =>
	String(option?.label ?? option?.nombre ?? option?.value ?? "");
const normalizeOperativosAdvancedFilters = (filters = []) =>
	(Array.isArray(filters) ? filters : [])
		.filter(
			(filterItem) =>
				typeof filterItem?.key === "string" &&
				OPERATIVOS_ADVANCED_FILTER_KEYS.has(filterItem.key)
		)
		.map((filterItem) => ({
			key: filterItem.key,
			seleccionado: Array.isArray(filterItem?.seleccionado)
				? filterItem.seleccionado.map((value) => String(value))
				: [],
		}));

const readPersistedSelectedFilters = () => {
	try {
		const rawValue = window.sessionStorage.getItem(OPERATIVOS_ADVANCED_FILTERS_SESSION_KEY);
		if (!rawValue) {
			return [];
		}

		const parsedValue = JSON.parse(rawValue);
		if (!Array.isArray(parsedValue)) {
			return [];
		}

		return normalizeOperativosAdvancedFilters(parsedValue);
	} catch (error) {
		return [];
	}
};

const persistSelectedFilters = (filters = []) => {
	try {
		const payload = normalizeOperativosAdvancedFilters(filters);

		window.sessionStorage.setItem(
			OPERATIVOS_ADVANCED_FILTERS_SESSION_KEY,
			JSON.stringify(payload)
		);
	} catch (error) {
		// Ignore session persistence failures in the UI.
	}
};

const mergeSelectedFilters = (nextFilters = [], previousFilters = []) =>
	(Array.isArray(nextFilters) ? nextFilters : []).map((filterItem) => {
		const previousFilter = (Array.isArray(previousFilters) ? previousFilters : []).find(
			(item) => item?.key === filterItem?.key
		);
		const availableValues = new Set(
			(Array.isArray(filterItem?.valores) ? filterItem.valores : []).map((option) =>
				getOptionValue(option)
			)
		);
		const selectedValues = (Array.isArray(previousFilter?.seleccionado)
			? previousFilter.seleccionado
			: []
		).filter((value) => availableValues.has(String(value)));

		const fallbackSelectedValues = Array.isArray(previousFilter?.seleccionado)
			? previousFilter.seleccionado.map((value) => String(value))
			: [];

		return {
			...filterItem,
			seleccionado: availableValues.size > 0 ? selectedValues : fallbackSelectedValues,
		};
	});

const addUniqueOption = (collection, value, label) => {
	const normalizedValue = String(value ?? "");
	if (normalizedValue === "" || collection.some((item) => item.value === normalizedValue)) {
		return;
	}

	collection.push({
		value: normalizedValue,
		label: String(label ?? normalizedValue),
	});
};

const buildOperativosFilters = (partes = []) => {
	const campanas = [];
	const empresas = [];
	const delegaciones = [];
	const cabos = [];

	(Array.isArray(partes) ? partes : []).forEach((parte) => {
		const idCampana = Number(parte?.id_campana || 0);
		const nombreCampana = String(parte?.nombre_campana || "").trim();
		addUniqueOption(
			campanas,
			idCampana > 0 ? String(idCampana) : "__sin_campana__",
			nombreCampana || "Sin campana"
		);

		const empresaValue = String(parte?.id_clienteG || "").trim();
		const nombreEmpresa = String(parte?.nombre_cliente || "").trim();
		addUniqueOption(
			empresas,
			empresaValue !== "" ? empresaValue : `empresa:${nombreEmpresa || "sin_empresa"}`,
			nombreEmpresa || empresaValue || "Sin empresa"
		);

		const idDelegacion = Number(parte?.id_delegacion || 0);
		const nombreDelegacion = String(parte?.nombre_delegacion || "").trim();
		addUniqueOption(
			delegaciones,
			idDelegacion > 0 ? String(idDelegacion) : "__sin_delegacion__",
			nombreDelegacion || "Sin delegacion"
		);

		const caboNombre =
			`${parte?.apellidos_cabo || ""} ${parte?.nombre_cabo || ""}`.trim() || "Sin cabo";
		const caboNif = String(parte?.nif_cabo || "").trim();
		const caboId = Number(parte?.id_cabo || 0);
		const caboValue =
			caboId > 0
				? String(caboId)
				: caboNif !== ""
					? `nif:${caboNif}`
					: `cabo:${caboNombre}`;
		const caboLabel = caboNif ? `${caboNombre} · ${caboNif}` : caboNombre;
		addUniqueOption(cabos, caboValue, caboLabel);
	});

	const sortByLabel = (left, right) =>
		String(left?.label || "").localeCompare(String(right?.label || ""), "es", {
			sensitivity: "base",
		});

	campanas.sort(sortByLabel);
	empresas.sort(sortByLabel);
	delegaciones.sort(sortByLabel);
	cabos.sort(sortByLabel);

	return [
		{ key: "campanas", nombre: "Campanas", valores: campanas },
		{ key: "empresas", nombre: "Empresas", valores: empresas },
		{ key: "delegaciones", nombre: "Delegaciones", valores: delegaciones },
		{ key: "cabos", nombre: "Cabos", valores: cabos },
		...STATIC_FILTERS,
	];
};

export const OperativosAdvancedFilters = ({
	open,
	onClose,
	partes = [],
	loaded = false,
	setCantidadFiltros,
	onFiltersChange,
	initialSelectedFilters,
}) => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
	const [arrFiltros, setArrFiltros] = useState(() =>
		mergeSharedAdvancedFilters(
			readPersistedSelectedFilters(),
			normalizeOperativosAdvancedFilters(initialSelectedFilters)
		)
	);
	const [loadingFilters, setLoadingFilters] = useState(false);
	const [filtersError] = useState("");

	const availableFilters = useMemo(() => buildOperativosFilters(partes), [partes]);

	const handleChange = (index) => (event) => {
		const {
			target: { value },
		} = event;

		const nextSelected = typeof value === "string" ? value.split(",") : value;

		setArrFiltros((currentFilters) =>
			currentFilters.map((filterItem, filterIndex) =>
				filterIndex === index
					? {
							...filterItem,
							seleccionado: nextSelected,
					  }
					: filterItem
			)
		);
	};

	const limpiarFiltros = () => {
		setArrFiltros((currentFilters) =>
			currentFilters.map((filterItem) => ({
				...filterItem,
				seleccionado: [],
			}))
		);
	};

	const borrarFiltro = (filterKey, selectedValue) => {
		setArrFiltros((currentFilters) =>
			currentFilters.map((filterItem) =>
				filterItem?.key === filterKey
					? {
							...filterItem,
							seleccionado: (Array.isArray(filterItem?.seleccionado)
								? filterItem.seleccionado
								: []
							).filter((value) => String(value) !== String(selectedValue)),
					  }
					: filterItem
			)
		);
	};

	useEffect(() => {
		if (!loaded) {
			setLoadingFilters(true);
			return;
		}

		setLoadingFilters(false);
		setArrFiltros((currentFilters) =>
			mergeSelectedFilters(availableFilters, currentFilters)
		);
	}, [availableFilters, loaded]);

	useEffect(() => {
		const total = arrFiltros.reduce(
			(accumulator, filtro) =>
				accumulator +
				(Array.isArray(filtro?.seleccionado) ? filtro.seleccionado.length : 0),
			0
		);

		setCantidadFiltros(total);
		if (typeof onFiltersChange === "function") {
			onFiltersChange(arrFiltros);
		}
	}, [arrFiltros, onFiltersChange, setCantidadFiltros]);

	useEffect(() => {
		persistSelectedFilters(arrFiltros);
		updateSharedPartesFiltersState({
			advancedFilters: arrFiltros,
		});
	}, [arrFiltros]);

	const filtrosContent = (
		<Grid
			container
			direction="column"
			justifyContent="space-between"
			alignItems="stretch"
			size={{ xs: 12 }}
			sx={{
				p: 1.5,
				height: "100%",
				width: "100%",
				minWidth: 0,
				maxWidth: "none",
			}}
		>
			<Grid container direction="column" size={{ xs: 12 }}>
				<Grid
					container
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					size={{ xs: 12 }}
				>
					<Typography variant="h5">Filtros</Typography>

					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Grid>

				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
					Opciones calculadas con los partes operativos cargados en pantalla.
				</Typography>

				<Divider sx={{ my: 1.5 }} />

				{loadingFilters && <LinearProgress sx={{ mb: 1.5 }} />}

				{!!filtersError && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						{filtersError}
					</Alert>
				)}

				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="stretch"
					size={{ xs: 12 }}
					spacing={2}
				>
					{arrFiltros.map((filtro, index) => {
						const selectedValues = Array.isArray(filtro?.seleccionado)
							? filtro.seleccionado
							: [];

						return (
							<Grid key={filtro.key} item size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
								<Grid
									container
									direction="column"
									justifyContent="start"
									alignItems="stretch"
									size={{ xs: 12 }}
									sx={{ height: "100%" }}
								>
									<FormControl variant="standard" fullWidth>
										<InputLabel id={`operativos-label-${filtro.key}`}>{filtro.nombre}</InputLabel>
										<Select
											labelId={`operativos-label-${filtro.key}`}
											id={`operativos-select-${filtro.key}`}
											multiple
											value={selectedValues}
											onChange={handleChange(index)}
											input={<Input label={filtro.nombre} />}
											renderValue={(selected) =>
												selected.length > 0
													? selected
															.map((value) =>
																getOptionLabel(
																	(Array.isArray(filtro?.valores) ? filtro.valores : []).find(
																		(option) =>
																			getOptionValue(option) === String(value)
																	)
																) || String(value)
															)
															.join(", ")
													: `Seleccionar ${filtro.nombre}`
											}
											MenuProps={MENU_PROPS}
										>
											{Array.isArray(filtro?.valores) && filtro.valores.length > 0 ? (
												filtro.valores.map((valor) => {
													const optionValue = getOptionValue(valor);
													const optionLabel = getOptionLabel(valor);

													return (
														<MenuItem
															key={`${filtro.key}-${optionValue}`}
															value={optionValue}
														>
															<Checkbox checked={selectedValues.includes(optionValue)} />
															<ListItemText primary={optionLabel} />
														</MenuItem>
													);
												})
											) : (
												<MenuItem disabled>
													<ListItemText primary="Sin opciones disponibles" />
												</MenuItem>
											)}
										</Select>
									</FormControl>

									<Grid
										container
										direction="row"
										justifyContent="start"
										alignItems="start"
										size={{ xs: 12 }}
										sx={{ gap: 0.75, mt: 1, alignContent: "flex-start", minHeight: 36 }}
									>
										{selectedValues.map((value) => {
											const option = (Array.isArray(filtro?.valores) ? filtro.valores : []).find(
												(item) => getOptionValue(item) === String(value)
											);

											return (
												<Chip
													key={`${filtro.key}-chip-${value}`}
													label={getOptionLabel(option) || String(value)}
													variant="filled"
													onDelete={() => borrarFiltro(filtro.key, value)}
													color="secondary"
												/>
											);
										})}
									</Grid>
								</Grid>
							</Grid>
						);
					})}

					{!loadingFilters && arrFiltros.length === 0 && (
						<Grid size={{ xs: 12 }}>
							<Alert severity="info">
								No hay filtros configurados para este rango.
							</Alert>
						</Grid>
					)}
				</Grid>
			</Grid>

			<Grid
				container
				direction="row"
				justifyContent="flex-end"
				alignItems="center"
				size={{ xs: 12 }}
			>
				<Button
					variant="outlined"
					color="primary"
					onClick={limpiarFiltros}
					fullWidth={!isDesktop}
					disabled={loadingFilters && arrFiltros.length === 0}
				>
					Limpiar filtros
				</Button>
			</Grid>
		</Grid>
	);

	if (isDesktop) {
		return (
			<Collapse in={open} timeout="auto" unmountOnExit sx={{ width: "100%" }}>
				<Paper
					variant="outlined"
					sx={{
						mt: 1.5,
						width: "100%",
						borderRadius: 2,
						overflow: "hidden",
					}}
				>
					{filtrosContent}
				</Paper>
			</Collapse>
		);
	}

	return (
		<Drawer
			open={open}
			onClose={onClose}
			anchor="right"
			PaperProps={{
				sx: {
					width: {
						xs: "86%",
						sm: "52%",
						md: "36%",
						lg: "30%",
						xl: "24%",
					},
				},
			}}
		>
			{filtrosContent}
		</Drawer>
	);
};
